import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import PropTypes from 'prop-types'

import find from 'lodash/find'

import Divider from '@material-ui/core/Divider'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'

import { useT } from '@galo-monitor/i18n'
import { logout, cog, user as userIcon, userGroup, multipleMarkers, officeBuildingCog, storeCog } from '@galo-monitor/icons'
import { AuthenticationActions } from '@galo-monitor/stores/authentication'
import { selectUser, selectUserApplications } from '@galo-monitor/stores/authentication/selectorAuthentication'
import { AVAILABLE_CONTEXTS } from '@galo-monitor/stores/context'
import { selectCurrentContextId, getContextSlugById } from '@galo-monitor/stores/context/selectorContext'
import colors from '@galo-monitor/styles/colors'
import Icon from '@galo-monitor/web-components/Icon'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

import useStyles from './styles'

const UserMenu = (props) => {
  const { onClosePopup, popoverRef } = props

  const dispatch = useCallback(useDispatch(), [])
  const { routes } = useRoutes()
  const history = useHistory()
  const classes = useStyles()
  const t = useT()

  const user = useSelector(selectUser)
  const userApplications = useSelector(selectUserApplications)

  const currentContextId = useSelector(selectCurrentContextId)

  const isCompanyUserInsideFarm = useMemo(
    () => (
      user.isCompany && getContextSlugById(currentContextId) !== AVAILABLE_CONTEXTS.company
    ),
    [currentContextId, user]
  )

  const isCompanyUserInCompanyEnvironment = useMemo(
    () => (
      user.isCompany && getContextSlugById(currentContextId) === AVAILABLE_CONTEXTS.company
    ),
    [currentContextId, user]
  )

  const isFarmUserInsideFarm = useMemo(
    () => (
      user.isFarm && [AVAILABLE_CONTEXTS.farm, AVAILABLE_CONTEXTS.property].includes(getContextSlugById(currentContextId))
    ),
    [currentContextId, user]
  )

  const userHasPermission = useCallback(
    (permission) => (
      find(userApplications, userApp => userApp.slug === permission)
    ),
    [userApplications]
  )

  const handleClick = useCallback(
    (onClick) => {
      onClick()
      onClosePopup()
      popoverRef?.current && popoverRef.current.onClose()
    },
    [onClosePopup, popoverRef]
  )

  const createOption = useCallback(
    ({ icon, text, onClick, className }) => (
      <>
        <ListItem
          className={ className ?? classes.item }
          onClick={ () => handleClick(onClick) }
          button
        >
          <ListItemIcon classes={ { root: classes.listItemIcon } }>
            {icon}
          </ListItemIcon>
          <ListItemText primary={ text } />
        </ListItem>
      </>
    ),
    [handleClick, classes]
  )

  return (
    <List className={ classes.container }>
      {createOption({
        icon: <Icon icon={ userIcon } color={ colors.black } />,
        text: t('profile', { howMany: 1 }),
        onClick: () => history.push(routes.userProfile.path)
      })}
      {routes.settings &&
        createOption({
          icon: <Icon icon={ cog } color={ colors.black } />,
          text: t('settings'),
          onClick: () => history.push(routes.settings.path)
        })
      }
      {(isCompanyUserInCompanyEnvironment && userHasPermission('gerenciamento-de-planos-farm')) &&
        createOption({
          icon: <Icon icon={ storeCog } color={ colors.black } />,
          text: t('farm settings'),
          onClick: () => history.push(routes.farmSettings.path)
        })
      }
      {(isCompanyUserInCompanyEnvironment && userHasPermission('gerenciamento-de-planos-farm')) &&
        createOption({
          icon: <Icon icon={ officeBuildingCog } color={ colors.black } />,
          text: t('company settings'),
          onClick: () => history.push(routes.companySettings.path)
        })
      }
      {((isCompanyUserInsideFarm || isFarmUserInsideFarm) && userHasPermission('gerenciamento-de-propriedades-farm')) &&
        createOption({
          icon: <Icon icon={ multipleMarkers } color={ colors.black } />,
          text: t('property', { howMany: 2 }),
          onClick: () => history.push(routes.properties.path)
        })
      }
      {((isCompanyUserInsideFarm || isFarmUserInsideFarm) && userHasPermission('gerenciamento-de-usuarios-farm')) &&
        createOption({
          icon: <Icon icon={ userGroup } color={ colors.black } />,
          text: t('user', { howMany: 2 }),
          onClick: () => history.push(routes.users.path)
        })
      }
      <Divider style={ { margin: 0 } } />
      {createOption({
        className: classes.danger,
        icon: <Icon icon={ logout } color={ colors.red } />,
        text: t('logout'),
        onClick: () => {
          dispatch(AuthenticationActions.logout())
        }
      })}
    </List>
  )
}

UserMenu.propTypes = {
  onClosePopup: PropTypes.func,
  popoverRef: PropTypes.any
}

UserMenu.defaultProps = {
  onClosePopup: () => {},
  popoverRef: {}
}

export default UserMenu
